import type { HuacaClient, QuotaSnapshot } from '@huaca/ui';
import type { FacetCounts, PostingDetail, PostingsPage } from './types';

/**
 * The tenders product API — three gated reads under `/api/tenders/…`.
 *
 * Free functions taking the client first, matching apps/claro/src/api.ts. The client has no
 * query-param builder, so every path is assembled here as a complete relative URL.
 */

/**
 * `requestWithQuota` rather than `request`, because these routes carry `X-RateLimit-*` headers and
 * the meter has to come from the response itself. The snapshot is read BEFORE the body is parsed,
 * which matters: parse throws on a non-2xx, and a 429 is exactly when the numbers are interesting.
 */
export async function listPostings(
  client: HuacaClient,
  params: URLSearchParams,
): Promise<{ page: PostingsPage; quota: QuotaSnapshot | null }> {
  const query = params.toString();
  const { data, quota } = await client.requestWithQuota<PostingsPage>(
    `/api/tenders/postings${query ? `?${query}` : ''}`,
  );
  return { page: data, quota };
}

/**
 * Two path segments, not one id. The stored key is `<slug>:<siteId>` and contains a colon; the
 * server rebuilds it and rejects a colon inside either half, so the composite key stays the
 * server's to construct.
 */
export async function getPosting(client: HuacaClient, slug: string, siteId: string): Promise<PostingDetail> {
  return client.request<PostingDetail>(
    `/api/tenders/postings/${encodeURIComponent(slug)}/${encodeURIComponent(siteId)}`,
  );
}

export async function getFacets(client: HuacaClient, params: URLSearchParams): Promise<FacetCounts> {
  const query = params.toString();
  const r = await client.request<{ facets: FacetCounts }>(`/api/tenders/facets${query ? `?${query}` : ''}`);
  return r.facets;
}
